'use client';

import { BaseAttribute } from '../../types/attributes';
import { CmsProduct } from '../../types/cmsProduct';
import { VerticalType } from '../../types/cmsTypes';
import { MaterialIconName } from '../../types/materialIconName';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import {
  formatAttributeItemValue,
  groupProductAttributes,
} from '../../utils/helpers';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import { CollapsibleSection } from '../CollapsibleSection/CollapsibleSection';
import LoanIcon from '../icons/LoanIcon';
import { RichText } from '../RichText';
import { TranslationKey, TRANSLATIONS } from './i18n';

export type SectionDetailProps = {
  isVisible: boolean;
  vertical?: VerticalType;
  long_description?: string;
  product: CmsProduct;
  title: TranslationKey;
  icon: MaterialIconName | undefined;
  toggleVisibility: () => void;
};

const SectionDetails: React.FC<SectionDetailProps> = ({
  isVisible,
  vertical,
  long_description,
  product,
  title,
  icon,
  toggleVisibility,
}) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const renderAttributeBlock = (
    attribute: string,
    title: string,
    items: Array<BaseAttribute>,
  ) => {
    if (items.length === 0) return null;

    return (
      <div className="space-x-spacing/gap/soft-8px" key={attribute}>
        <div className="mhg-alpha-bullet-2">{title}</div>
        <ul>
          {items.map((item, i) =>
            item.name?.name === 'REQUIRED_DOCUMENTS' ? (
              <div key={item.name.name} className="mhg-alpha-body-2">
                <RichText>
                  {formatAttributeItemValue(item, false, true)}
                </RichText>
              </div>
            ) : (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${product.title}_${i}_${item.name?.uid}`}
                className="mhg-alpha-body-2"
              >
                {item.label}: {formatAttributeItemValue(item, false, true)}
              </div>
            ),
          )}
        </ul>
      </div>
    );
  };

  const productAttributesGroup = groupProductAttributes(product);

  return (
    <CollapsibleSection
      id={`section-details-${product.uid}`}
      isOpen={isVisible}
      onToggle={toggleVisibility}
      iconName={icon}
      iconClassName="overflow-visible size-[18px] md:size-[22px] md:mb-0 md:mr-2"
      content={
        <div className="p-3">
          <div className="grid grid-cols-1 gap-spacing/gap/soft-12px font-medium text-sm lg:text-base">
            {productAttributesGroup.repayment &&
              renderAttributeBlock(
                'repayment',
                staticText.repayment_details,
                productAttributesGroup.repayment,
              )}

            {productAttributesGroup.fees &&
              renderAttributeBlock(
                'fees',
                staticText.fees,
                productAttributesGroup.fees,
              )}

            {productAttributesGroup.eligibility &&
              renderAttributeBlock(
                'eligibility',
                staticText.eligibility,
                productAttributesGroup.eligibility,
              )}

            {productAttributesGroup.requiredDocuments &&
              vertical === 'PERSONAL_LOANS' &&
              renderAttributeBlock(
                'required_documents',
                staticText.required_documents,
                productAttributesGroup.requiredDocuments,
              )}
          </div>
          {long_description && long_description !== '' && (
            <div className="mt-3">
              <div className="mhg-alpha-bullet-2">
                {staticText.key_features}
              </div>
              <RichText className="w-full overflow-x-hidden">
                {long_description ?? ''}
              </RichText>
            </div>
          )}
        </div>
      }
    >
      {vertical === 'PERSONAL_LOANS' && <LoanIcon />}
      <span className="mhg-alpha-bullet-2 lg:mhg-alpha-bullet-1">
        {tr(title, staticText)}
      </span>
    </CollapsibleSection>
  );
};

export default SectionDetails;
